import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Radio } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { formJsonFieldFormatter, formJsonFieldNormalize } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { MASTER_NODES_FIELD, WORKER_NODES_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';

import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { GroupSpecSection } from '../maas-node-groups/group-spec-card';
import cardStyles from '../maas-node-groups/group-spec-card/group-spec-card.module.less';
import {
  TaskDagCanvas,
  connectTaskDagDependency,
  disconnectTaskDagDependency,
  reconnectTaskDagDependency,
  removeTaskDagItem,
  renameTaskDagItem,
} from './TaskDagCanvas';
import { TaskDagLevelList } from './TaskDagLevelList';
import { TaskStepAddCard, TaskStepCard } from './TaskStepCard';
import { TaskStepModal } from './TaskStepModal';
import type { MaasTaskTemplateBlockProps } from './blockTypes';
import { MAAS_TASK_TEMPLATE_ACTION_KEYS, MAAS_TASK_TEMPLATE_FIELD_KEYS } from './fieldKeys';
import { type MaasTaskTemplateStep, TASK_TEMPLATE_FIELD } from './types';
import { parseTaskTemplateField } from './utils';

const FormItem = Form.Item;

/** 从 masterNodes / workerNodes 按角色提取节点组名称（供步骤弹窗「指定运行节点」筛选） */
function collectNodeGroupNamesByRole(masterNodes: unknown, workerNodes: unknown): { head: string[]; worker: string[] } {
  const collectNames = (raw: unknown): string[] => {
    const names = new Set<string>();
    parseNodesField(raw).forEach((node) => {
      const name = String(node?.name ?? '').trim();
      if (name) names.add(name);
    });
    return [...names];
  };
  return {
    head: collectNames(masterNodes),
    worker: collectNames(workerNodes),
  };
}

function writeTaskTemplate(form: FormInstance, field: string, steps: MaasTaskTemplateStep[]) {
  form.setFieldValue(field, steps.length ? JSON.stringify(steps) : undefined);
}

/** 任务模板：步骤卡片列表 + 添加/编辑弹窗 */
export function MaasTaskTemplateBlock({ form, options, ...control }: MaasTaskTemplateBlockProps) {
  const { t } = useTranslation();
  const listField = options?.listField || TASK_TEMPLATE_FIELD;
  const masterNodesField = options?.masterNodesField || MASTER_NODES_FIELD;
  const workerNodesField = options?.workerNodesField || WORKER_NODES_FIELD;
  const sectionTitle = options?.sectionTitle || t('data-center:maas.task.template');
  const rawTemplate = Form.useWatch(listField, form);
  const masterNodes = Form.useWatch(masterNodesField, form);
  const workerNodes = Form.useWatch(workerNodesField, form);
  const steps = useMemo(() => parseTaskTemplateField(rawTemplate), [rawTemplate]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [view, setView] = useState<'dag' | 'list'>('dag');
  const [pendingDependency, setPendingDependency] = useState('');

  const readonly = control.blockMode === 'preview';
  const canAdd = isActionVisible(MAAS_TASK_TEMPLATE_ACTION_KEYS.add, control) && !readonly;
  const canEdit = isActionVisible(MAAS_TASK_TEMPLATE_ACTION_KEYS.edit, control) && !readonly;
  const canRemove = isActionVisible(MAAS_TASK_TEMPLATE_ACTION_KEYS.remove, control) && !readonly;
  const scriptHint = options?.scriptHint;

  const nodeGroupOptionsByRole = useMemo(() => collectNodeGroupNamesByRole(masterNodes, workerNodes), [masterNodes, workerNodes]);

  /** 允许选择任意不会与当前步骤形成循环的任务。 */
  const dependableStepNames = useMemo(() => {
    if (editingIndex < 0) return steps.map((step) => step.name);
    const current = steps[editingIndex];
    return steps
      .filter(
        (step) =>
          step.name !== current.name &&
          (current.dependsOn?.includes(step.name) || !!connectTaskDagDependency(steps, step.name, current.name)),
      )
      .map((step) => step.name);
  }, [editingIndex, steps]);

  const openAddModal = useCallback((dependency = '') => {
    setPendingDependency(dependency);
    setEditingIndex(-1);
    setModalVisible(true);
  }, []);

  const openEditModal = useCallback((index: number) => {
    setEditingIndex(index);
    setModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setEditingIndex(-1);
    setPendingDependency('');
  }, []);

  const handleModalOk = useCallback(
    (step: MaasTaskTemplateStep) => {
      const next = [...steps];
      if (editingIndex >= 0) {
        const renamed = renameTaskDagItem(next, next[editingIndex].name, step.name);
        if (!renamed) return;
        renamed[editingIndex] = step;
        writeTaskTemplate(form, listField, renamed);
        closeModal();
        return;
      } else {
        next.push({ ...step, dependsOn: [...new Set([...(step.dependsOn || []), pendingDependency].filter(Boolean))] });
      }
      writeTaskTemplate(form, listField, next);
      closeModal();
    },
    [closeModal, editingIndex, form, listField, pendingDependency, steps],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const next = removeTaskDagItem(steps, steps[index]?.name || '');
      writeTaskTemplate(form, listField, next);
    },
    [form, listField, steps],
  );

  if (!isFieldVisible(MAAS_TASK_TEMPLATE_FIELD_KEYS.list, control)) {
    return null;
  }

  return (
    <>
      <GroupSpecSection title={sectionTitle} hint={t('data-center:maas.task.template.desc')}>
        <div className={cardStyles.section} style={{ marginTop: 0 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px 16px',
              marginBottom: 8,
            }}
          >
            <div className={cardStyles.sectionHead} style={{ flex: '1 1 320px', minWidth: 0, marginBottom: 0 }}>
              <span className={cardStyles.sectionTitle}>{t('data-center:maas.task.step.section')}</span>
              <span className={cardStyles.sectionHint}>{t(`data-center:maas.task.config.${view}.hint`)}</span>
            </div>
            <Radio.Group type="button" value={view} onChange={setView} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Radio value="dag">DAG</Radio>
              <Radio value="list">{t('data-center:maas.task.step.list')}</Radio>
            </Radio.Group>
          </div>
          {view === 'dag' ? (
            steps.length ? (
              <TaskDagCanvas
                tasks={steps}
                editable={!readonly && (canAdd || canEdit || canRemove)}
                onAdd={canAdd ? openAddModal : undefined}
                onEdit={
                  canEdit
                    ? (name) => {
                        const index = steps.findIndex((step) => step.name === name);
                        if (index >= 0) openEditModal(index);
                      }
                    : undefined
                }
                onDelete={
                  canRemove
                    ? (name) => {
                        const index = steps.findIndex((step) => step.name === name);
                        if (index >= 0) handleDelete(index);
                      }
                    : undefined
                }
                isValidConnection={canEdit ? (source, target) => !!connectTaskDagDependency(steps, source, target) : undefined}
                onConnect={
                  canEdit
                    ? (source, target) => {
                        const next = connectTaskDagDependency(steps, source, target);
                        if (next) writeTaskTemplate(form, listField, next);
                      }
                    : undefined
                }
                onDisconnect={
                  canEdit
                    ? (source, target) => writeTaskTemplate(form, listField, disconnectTaskDagDependency(steps, source, target))
                    : undefined
                }
                onReconnect={
                  canEdit
                    ? (oldSource, oldTarget, source, target) => {
                        const next = reconnectTaskDagDependency(steps, oldSource, oldTarget, source, target);
                        if (!next) return false;
                        writeTaskTemplate(form, listField, next);
                        return true;
                      }
                    : undefined
                }
              />
            ) : canAdd ? (
              <TaskStepAddCard onClick={() => openAddModal()} />
            ) : null
          ) : (
            <TaskDagLevelList
              tasks={steps}
              levelLabel={(level) => t('data-center:maas.task.config.level', { level })}
              renderTask={(step, index) => (
                <TaskStepCard
                  key={step.name}
                  step={step}
                  readonly={readonly || !canEdit}
                  onEdit={canEdit ? () => openEditModal(index) : () => undefined}
                  onDelete={canRemove ? () => handleDelete(index) : () => undefined}
                />
              )}
              footer={canAdd ? <TaskStepAddCard onClick={() => openAddModal()} /> : null}
            />
          )}
        </div>
      </GroupSpecSection>

      <FormItem field={listField} hidden triggerPropName="value" formatter={formJsonFieldFormatter} normalize={formJsonFieldNormalize}>
        <Input />
      </FormItem>

      {canAdd || canEdit ? (
        <TaskStepModal
          visible={modalVisible}
          initialValues={editingIndex >= 0 ? steps[editingIndex] : undefined}
          stepIndex={editingIndex >= 0 ? editingIndex : steps.length}
          nodeGroupOptionsByRole={nodeGroupOptionsByRole}
          dependableStepNames={dependableStepNames}
          stepNames={steps.map((step) => step.name)}
          scriptHint={scriptHint}
          onOk={handleModalOk}
          onCancel={closeModal}
        />
      ) : null}
    </>
  );
}

export default MaasTaskTemplateBlock;
